'use client';

import { useEffect, useState, useTransition } from 'react';
import { useSuspenseQuery } from '@apollo/client';
import { Link } from '@/i18n/routing';
import { useLocale, useTranslations } from 'next-intl';
import { differenceInDays } from 'date-fns';
import Gallery from '@/component/photoGallery/index';
import { ErrorBoundary } from '@/components/base/Layout/ErrorBoundary';
import NotFound from '@/app/[locale]/error';
import { SearchListingDetailsQuery } from '@/query';
import {
  getListingDetailsVariables,
  buildSearchQuery,
  prepareBookingParams,
  parseAmenities,
} from '@/utils';
import {
  makeGuestsValid,
  makeRoomsValid,
  makeRatingValid,
} from '@/utils/guests';
import { useListingSearchStore } from '@/store';
import { useParams, useSearchParams } from 'next/navigation';
import { useSearchFromUrl } from '@/components/base/SearchListing';
import { AvailabilityWidget } from './AvailabilityWidget';
import { ListingDetailsInfo } from './Info';
import { Schema } from './Schema';

export const ListingDetails = () => {
  useSearchFromUrl();

  const { location, setLocation, setGuests } = useListingSearchStore();
  const t = useTranslations();
  const locale = useLocale();
  const searchParams = useSearchParams();
  const params = useParams();

  const initialGuests = makeGuestsValid({
    adults: searchParams.getAll('adults'),
    infants: searchParams.getAll('infants'),
    children: searchParams.getAll('children'),
  });
  const initialPets = searchParams.get('pets');
  const locationType = searchParams.get('locationType');
  const locationName = searchParams.get('locationName');
  const locationId = searchParams.get('locationId');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const tab = searchParams.get('tab');
  const initialRooms = makeRoomsValid({
    bedrooms: searchParams.getAll('bedrooms'),
    bathrooms: searchParams.getAll('bathrooms'),
    beds: searchParams.getAll('beds'),
  });
  const initialAmenities = parseAmenities({
    amenities: searchParams.get('amenities'),
  });
  const initialRating = makeRatingValid(searchParams.get('rating'));
  const priceMin = searchParams.get('priceMin');
  const priceMax = searchParams.get('priceMax');
  const id = params.slug;
  const hasDates = startDate && endDate;
  const { priceEndDate, priceStartDate } = getListingDetailsVariables(
    startDate,
    endDate
  );
  const daysAmount =
    differenceInDays(new Date(priceEndDate), new Date(priceStartDate)) || 1;
  const [backParams] = useState(() => {
    const listParams = prepareBookingParams({
      location: {
        type: locationType,
        name: locationName,
        id: locationId,
      },
      date: {
        startDate,
        endDate,
      },
      price: {
        min: priceMin,
        max: priceMax,
      },
      rating: initialRating,
      rooms: initialRooms,
      amenities: initialAmenities,
      pets: initialPets,
      guests: initialGuests,
    });

    return (
      (tab ? `/filters/${tab}` : '') +
      buildSearchQuery({
        ...listParams,
        date: listParams.dateRange,
      })
    );
  });
  const [priceLoading, startTransition] = useTransition();

  const { data, error } = useSuspenseQuery(SearchListingDetailsQuery, {
    variables: {
      slug: id,
      locale,
    },
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (!id) return;

    if (locationId && location?.id && (locationId !== location?.id)) {
      setLocation({ type: locationType, name: locationName, id: locationId });
    }

    setGuests(initialGuests);
  }, [id]);

  const copyLinkPath = async () => {
    await navigator.clipboard.writeText(document.URL);

    alert('Link copied to clipboard');
  };

  const isEmpty = !data?.showListing || error;

  if (isEmpty) {
    return <NotFound withHeader={false} />;
  }

  return (
    <>
      {data?.showListing && (
        <>
          <Schema data={data?.showListing} />

          <div className="flex justify-between bg-white pr-4 pl-2 py-1 max-sm:!flex-row sm:!hidden">
            <Link
              href={`/listings${backParams}`}
              className="btn btn-text btn-sm btn-icon self-end sm:pr-6"
            >
              <i className="icon-arrow_alt_left text-[24px]" />
              <span className="pl-2">{t('apartment.hero.view-all')}</span>
            </Link>
            <button
              onClick={copyLinkPath}
              className="btn btn-text btn-sm btn-icon self-end sm:pr-6"
            >
              <i className="icon-share text-[24px]" />
            </button>
          </div>

          <div className="flex flex-col">
            <section className="container flex gap-2.5 w-full justify-between items-center max-sm:pb-[18px] sm:pb-6 max-sm:order-2 max-sm:pt-6 sm:pt-6 pb-6 3xl:rounded-t-10">
              <div className="flex flex-col flex-1 max-sm:gap-[6px] sm:gap-3">
                {data.showListing.name && (
                  <h1
                    className="text-primary-800 max-sm:text-2xl text-[40px] font-semibold leading-[normal]"
                    data-testid="listing-title"
                  >
                    {data.showListing.name}
                  </h1>
                )}
              </div>
              <div className="self-end flex gap-2.5">
                <Link
                  href={`/listings${backParams}`}
                  className="btn btn-text btn-icon sm:pr-6 max-sm:hidden"
                >
                  <i className="icon-file_dock_search text-[24px]" />
                  <span className="sm:ml-2 max-sm:hidden">
                    {t('apartment.hero.view-all')}
                  </span>
                </Link>
                <button
                  onClick={copyLinkPath}
                  className="btn btn-text btn-icon self-end sm:pr-6 max-sm:hidden"
                >
                  <i className="icon-share text-[24px]" />
                  <span className="sm:ml-2 max-sm:hidden">
                    {t('apartment.hero.link')}
                  </span>
                </button>
              </div>
            </section>

            <div className="max-sm:px-0 container max-md:order-1">
              <Gallery images={data.showListing.previewImages} />
            </div>

            <div className="container max-md:order-3 3xl:rounded-b-10">
              <section className="flex max-sm:flex-col gap-8 lg:gap-16 sm:pt-6 mb-16">
                <section className="flex-initial min-w-0">
                  <ListingDetailsInfo
                    locationName={locationName}
                    priceStartDate={startDate}
                    priceEndDate={endDate}
                    details={data.showListing}
                    daysAmount={daysAmount}
                    priceLoading={priceLoading}
                    startTransition={startTransition}
                  />
                </section>

                <section className="max-sm:fixed max-md:z-[12] max-sm:bottom-[-1px] max-sm:inset-x-0 sm:max-w-[280px] md:max-w-[380px] shrink-0 md:w-1/2 lg:max-w-[400px] z-10">
                  <ErrorBoundary
                    tags={{ feature: 'listing_details_payment_info' }}
                  >
                    <AvailabilityWidget
                      key={`${startDate}-${endDate}`}
                      daysAmount={daysAmount}
                      details={data.showListing}
                      priceStartDate={startDate}
                      priceEndDate={endDate}
                      hasDates={hasDates}
                      priceLoading={priceLoading}
                      startTransition={startTransition}
                    />
                  </ErrorBoundary>
                </section>
              </section>
            </div>
          </div>
        </>
      )}
    </>
  );
};
